import {
  parseProductSyncMarkers,
  sanitizeProductSyncLog,
} from '../Settings/integrations/product-sync-utils';

const initialState = {
  integrations: [],
  loading: false,
  loaded: false,
  loadError: '',
  ti2token: '',
  templates: {},
  companySettings: {},
  companySettingsLoading: {},
  companySettingsLoaded: {},
  companySettingsErrors: {},
  tokens: {},
  editor: { open: false, integration: null },
  companyDialog: { open: false, integrationId: '' },
  removeDialog: { open: false, integration: null },
  saving: false,
  savingCompanySettings: false,
  removing: false,
  validation: {},
  refreshing: {},
  tourplanRefresh: {},
  productFilesLocations: {},
  productSync: {
    instances: [],
    instancesLoading: false,
    instancesLoaded: false,
    instancesError: '',
    detail: null,
    detailInstanceKey: '',
    detailCompanyId: '',
    detailLoading: false,
    detailLoaded: false,
    detailError: '',
    runStarting: false,
    autoSyncUpdating: false,
    inspection: null,
    inspectionLoading: false,
    inspectionError: '',
    polling: null,
    job: null,
    markers: [],
    logs: [],
  },
};

const integrationKey = ({ integrationId, hint }) => `${integrationId}::${hint}`;

const setRequestState = (requests, args, value) => {
  const integration = args && args[0];
  if (!integration || !integration.integrationId || !integration.hint) return requests;
  return {
    ...requests,
    [integrationKey(integration)]: value,
  };
};

const normalizeTemplate = payload => {
  const template = payload && payload.template ? payload.template : {};
  return Object.keys(template).reduce((result, field) => {
    const definition = template[field] || {};
    return {
      ...result,
      [field]: {
        ...definition,
        regExp: definition.regExp
          ? new RegExp(definition.regExp.source, definition.regExp.flags)
          : undefined,
      },
    };
  }, {});
};

const getApiErrorMessage = (
  payload,
  fallback = 'Unable to complete the product sync request.',
) => {
  if (typeof payload === 'string') return payload;
  return (
    payload
    && payload.json
    && payload.json.error
    && payload.json.error.message
  ) || (
    payload
    && payload.json
    && payload.json.message
  ) || (payload && payload.message) || fallback;
};

const productSyncOwnerFromArgs = args => {
  if (!Array.isArray(args)) return {};
  return args.reduce((owner, arg) => (
    arg && typeof arg === 'object' && (arg.instanceKey || arg.companyId)
      ? { ...owner, instanceKey: arg.instanceKey || owner.instanceKey, companyId: arg.companyId || owner.companyId }
      : owner
  ), {});
};

const ownsProductSyncState = (productSync, owner) => (
  (!owner.instanceKey || owner.instanceKey === productSync.detailInstanceKey)
  && (!owner.companyId || owner.companyId === productSync.detailCompanyId)
);

export default function integrationSettings(state = initialState, action) {
  const { type, payload, args } = action;
  switch (type) {
    case 'genAi_integrationSettings_setTi2Token':
      return { ...state, ti2token: payload };
    case 'genAi_integrationSettings_loadIntegrations_STARTED':
      return { ...state, loading: true, loadError: '' };
    case 'genAi_integrationSettings_loadIntegrations_SUCCESS':
      return {
        ...state,
        loading: false,
        loaded: true,
        integrations: (payload.userAppKeys || []).map(integration => ({
          ...integration,
          configuration: null,
        })),
      };
    case 'genAi_integrationSettings_loadIntegrations_ERROR':
      return { ...state, loading: false, loaded: true, loadError: payload };
    case 'genAi_integrationSettings_loadTemplate_STARTED': {
      const integrationId = args[0].integrationId;
      return {
        ...state,
        templates: {
          ...state.templates,
          [integrationId]: {
            ...(state.templates[integrationId] || {}),
            loading: true,
          },
        },
      };
    }
    case 'genAi_integrationSettings_loadTemplate_SUCCESS': {
      const integrationId = args[0].integrationId;
      return {
        ...state,
        templates: {
          ...state.templates,
          [integrationId]: { loading: false, loaded: true, template: normalizeTemplate(payload) },
        },
      };
    }
    case 'genAi_integrationSettings_loadTemplate_ERROR': {
      const integrationId = args[0].integrationId;
      return {
        ...state,
        templates: {
          ...state.templates,
          [integrationId]: { loading: false, loaded: true, template: {} },
        },
      };
    }
    case 'genAi_integrationSettings_loadCompanySettings_STARTED':
      return {
        ...state,
        companySettingsLoading: {
          ...state.companySettingsLoading,
          [args[0].integrationId]: true,
        },
        companySettingsErrors: {
          ...state.companySettingsErrors,
          [args[0].integrationId]: false,
        },
      };
    case 'genAi_integrationSettings_loadCompanySettings_SUCCESS':
      return {
        ...state,
        companySettings: {
          ...state.companySettings,
          [args[0].integrationId]: payload.settings || {},
        },
        companySettingsLoading: {
          ...state.companySettingsLoading,
          [args[0].integrationId]: false,
        },
        companySettingsLoaded: {
          ...state.companySettingsLoaded,
          [args[0].integrationId]: true,
        },
      };
    case 'genAi_integrationSettings_loadCompanySettings_ERROR':
      return {
        ...state,
        companySettingsLoading: {
          ...state.companySettingsLoading,
          [args[0].integrationId]: false,
        },
        companySettingsErrors: {
          ...state.companySettingsErrors,
          [args[0].integrationId]: true,
        },
      };
    case 'genAi_integrationSettings_loadToken_STARTED': {
      const key = integrationKey(args[0]);
      return {
        ...state,
        tokens: {
          ...state.tokens,
          [key]: {
            error: false,
            loaded: false,
            loading: true,
            token: (state.tokens[key] || {}).token || {},
          },
        },
      };
    }
    case 'genAi_integrationSettings_loadToken_SUCCESS':
      return {
        ...state,
        tokens: {
          ...state.tokens,
          [integrationKey(args[0])]: {
            error: false,
            loaded: true,
            loading: false,
            token: payload.token || {},
          },
        },
      };
    case 'genAi_integrationSettings_loadToken_ERROR': {
      const key = integrationKey(args[0]);
      return {
        ...state,
        tokens: {
          ...state.tokens,
          [key]: {
            error: true,
            loaded: true,
            loading: false,
            token: (state.tokens[key] || {}).token || {},
          },
        },
      };
    }
    case 'genAi_integrationSettings_openEditor':
      return { ...state, editor: { open: true, integration: payload || null } };
    case 'genAi_integrationSettings_closeEditor':
      return { ...state, editor: initialState.editor };
    case 'genAi_integrationSettings_openCompanyDialog':
      return { ...state, companyDialog: { open: true, integrationId: payload.integrationId } };
    case 'genAi_integrationSettings_closeCompanyDialog':
      return { ...state, companyDialog: initialState.companyDialog };
    case 'genAi_integrationSettings_openRemoveDialog':
      return { ...state, removeDialog: { open: true, integration: payload } };
    case 'genAi_integrationSettings_closeRemoveDialog':
      return { ...state, removeDialog: initialState.removeDialog };
    case 'genAi_integrationSettings_save_STARTED':
      return { ...state, saving: true };
    case 'genAi_integrationSettings_save_SUCCESS':
    case 'genAi_integrationSettings_save_ERROR':
      return { ...state, saving: false };
    case 'genAi_integrationSettings_saveCompanySettings_STARTED':
      return { ...state, savingCompanySettings: true };
    case 'genAi_integrationSettings_saveCompanySettings_SUCCESS':
    case 'genAi_integrationSettings_saveCompanySettings_ERROR':
      return { ...state, savingCompanySettings: false };
    case 'genAi_integrationSettings_remove_STARTED':
      return { ...state, removing: true };
    case 'genAi_integrationSettings_remove_SUCCESS':
    case 'genAi_integrationSettings_remove_ERROR':
      return { ...state, removing: false };
    case 'genAi_integrationSettings_validate_STARTED':
      return {
        ...state,
        validation: setRequestState(state.validation, args, { loading: true }),
      };
    case 'genAi_integrationSettings_validate_SUCCESS':
      return {
        ...state,
        validation: setRequestState(state.validation, args, {
          loading: false,
          valid: Boolean(payload.valid),
        }),
      };
    case 'genAi_integrationSettings_validate_ERROR':
      return {
        ...state,
        validation: setRequestState(state.validation, args, { loading: false, valid: false }),
      };
    case 'genAi_integrationSettings_forceRefresh_STARTED':
      return {
        ...state,
        refreshing: setRequestState(state.refreshing, args, true),
        tourplanRefresh: args && args[0] && args[0].integrationId === 'tourplannx'
          ? {
            ...state.tourplanRefresh,
            [integrationKey(args[0])]: { status: 'running', error: '' },
          } : state.tourplanRefresh,
      };
    case 'genAi_integrationSettings_tourplanRefreshUpdate':
      if (!payload || !payload.integrationId || !payload.hint) return state;
      return {
        ...state,
        tourplanRefresh: {
          ...state.tourplanRefresh,
          [integrationKey(payload)]: payload,
        },
      };
    case 'genAi_integrationSettings_productFilesLoad_SUCCESS':
      return {
        ...state,
        productFilesLocations: ((payload && payload.locations) || []).reduce((result, location) => ({
          ...result,
          [integrationKey(location)]: location,
        }), {}),
      };
    case 'genAi_integrationSettings_forceRefresh_SUCCESS':
      return {
        ...state,
        refreshing: setRequestState(state.refreshing, args, false),
      };
    case 'genAi_integrationSettings_forceRefresh_ERROR':
      return {
        ...state,
        refreshing: setRequestState(state.refreshing, args, false),
        tourplanRefresh: args && args[0] && args[0].integrationId === 'tourplannx'
          ? {
            ...state.tourplanRefresh,
            [integrationKey(args[0])]: {
              status: 'failed',
              error: getApiErrorMessage(
                payload,
                'Unable to complete the product refresh request.',
              ),
            },
          } : state.tourplanRefresh,
      };
    case 'genAi_productSync_instances_STARTED':
      return {
        ...state,
        productSync: {
          ...state.productSync,
          instancesLoading: true,
          instancesError: '',
        },
      };
    case 'genAi_productSync_instances_SUCCESS':
      return {
        ...state,
        productSync: {
          ...state.productSync,
          instances: payload && Array.isArray(payload.instances) ? payload.instances : [],
          instancesLoading: false,
          instancesLoaded: true,
          instancesError: '',
        },
      };
    case 'genAi_productSync_instances_ERROR':
      return {
        ...state,
        productSync: {
          ...state.productSync,
          instancesLoading: false,
          instancesLoaded: true,
          instancesError: getApiErrorMessage(payload),
        },
      };
    case 'genAi_productSync_enterInstance':
      return {
        ...state,
        productSync: {
          ...state.productSync,
          detail: state.productSync.detailInstanceKey === payload.instanceKey
            ? state.productSync.detail : null,
          detailInstanceKey: payload.instanceKey,
          detailCompanyId: payload.companyId,
          detailLoading: true,
          detailLoaded: false,
          detailError: '',
          inspection: null,
          inspectionLoading: false,
          inspectionError: '',
          polling: null,
          job: null,
          markers: [],
          logs: [],
        },
      };
    case 'genAi_productSync_leaveInstance':
      if (payload && !ownsProductSyncState(state.productSync, payload)) return state;
      return {
        ...state,
        productSync: {
          ...state.productSync,
          detail: null,
          detailInstanceKey: '',
          detailCompanyId: '',
          detailLoading: false,
          detailLoaded: false,
          detailError: '',
          runStarting: false,
          autoSyncUpdating: false,
          inspection: null,
          inspectionLoading: false,
          inspectionError: '',
          polling: null,
          job: null,
          markers: [],
          logs: [],
        },
      };
    case 'genAi_productSync_detail_STARTED': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      return {
        ...state,
        productSync: {
          ...state.productSync,
          detailLoading: true,
          detailError: '',
        },
      };
    }
    case 'genAi_productSync_detail_SUCCESS': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      return {
        ...state,
        productSync: {
          ...state.productSync,
          detail: payload,
          detailLoading: false,
          detailLoaded: true,
          detailError: '',
        },
      };
    }
    case 'genAi_productSync_detail_ERROR': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      return {
        ...state,
        productSync: {
          ...state.productSync,
          detailLoading: false,
          detailLoaded: true,
          detailError: getApiErrorMessage(payload),
        },
      };
    }
    case 'genAi_productSync_startRun_STARTED': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      return {
        ...state,
        productSync: { ...state.productSync, runStarting: true },
      };
    }
    case 'genAi_productSync_startRun_SUCCESS': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      const request = Array.isArray(args) && args[1] ? args[1] : {};
      const run = payload && payload.run;
      const polling = run && run.runId ? {
        ...owner,
        runId: run.runId,
        action: request.action,
      } : state.productSync.polling;
      return {
        ...state,
        productSync: {
          ...state.productSync,
          runStarting: false,
          polling,
          ...(polling ? { job: { status: 'pending' }, markers: [], logs: [] } : {}),
        },
      };
    }
    case 'genAi_productSync_startRun_ERROR': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      return {
        ...state,
        productSync: { ...state.productSync, runStarting: false },
      };
    }
    case 'genAi_productSync_pollingStarted':
      if (!ownsProductSyncState(state.productSync, payload)) return state;
      return {
        ...state,
        productSync: {
          ...state.productSync,
          polling: payload,
          job: { status: 'pending' },
          markers: [],
          logs: [],
        },
      };
    case 'genAi_productSync_jobProgress': {
      if (!ownsProductSyncState(state.productSync, payload)) return state;
      const { polling } = state.productSync;
      if (
        !polling
        || payload.runId !== polling.runId
      ) return state;
      const logs = sanitizeProductSyncLog(payload.jobResp && payload.jobResp.log);
      return {
        ...state,
        productSync: {
          ...state.productSync,
          job: payload.jobResp || null,
          markers: parseProductSyncMarkers(logs, payload.runId),
          logs,
        },
      };
    }
    case 'genAi_productSync_pollingStopped': {
      if (!ownsProductSyncState(state.productSync, payload)) return state;
      const { polling } = state.productSync;
      if (
        !polling
        || payload.runId !== polling.runId
      ) return state;
      return {
        ...state,
        productSync: { ...state.productSync, polling: null },
      };
    }
    case 'genAi_productSync_autoSync_STARTED': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      return {
        ...state,
        productSync: { ...state.productSync, autoSyncUpdating: true },
      };
    }
    case 'genAi_productSync_autoSync_SUCCESS':
    case 'genAi_productSync_autoSync_ERROR': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      return {
        ...state,
        productSync: { ...state.productSync, autoSyncUpdating: false },
      };
    }
    case 'genAi_productSync_inspection_STARTED': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      return {
        ...state,
        productSync: {
          ...state.productSync,
          inspectionLoading: true,
          inspectionError: '',
        },
      };
    }
    case 'genAi_productSync_inspection_SUCCESS': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      return {
        ...state,
        productSync: {
          ...state.productSync,
          inspection: payload && payload.inspection ? payload.inspection : null,
          inspectionLoading: false,
          inspectionError: '',
        },
      };
    }
    case 'genAi_productSync_inspection_ERROR': {
      const owner = productSyncOwnerFromArgs(args);
      if (!ownsProductSyncState(state.productSync, owner)) return state;
      return {
        ...state,
        productSync: {
          ...state.productSync,
          inspectionLoading: false,
          inspectionError: getApiErrorMessage(payload),
        },
      };
    }
    default:
      return state;
  }
}

export {
  getApiErrorMessage,
  initialState,
  integrationKey,
  normalizeTemplate,
  ownsProductSyncState,
  productSyncOwnerFromArgs,
};
